/**
 * AdminChatModeration — list of current author blocks + audit log + bag tamper events.
 * Wired into AdminDashboard via the "Moderacao" tab.
 */
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Prohibit, Trash, ShieldCheck, Clock, ArrowCounterClockwise, Lock, WarningCircle, Funnel, X } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_LABELS = {
  delete_message: { label: "Mensagem apagada", color: "#dc2626", bg: "rgba(220,38,38,0.08)", icon: Trash },
  block_author: { label: "Autor bloqueado", color: "var(--accent)", bg: "rgba(217,108,74,0.08)", icon: Prohibit },
  unblock_author: { label: "Autor desbloqueado", color: "var(--success)", bg: "rgba(62,102,86,0.08)", icon: ShieldCheck },
  blocked_author_retry: { label: "Tentativa de autor bloqueado", color: "#dc2626", bg: "rgba(220,38,38,0.12)", icon: WarningCircle },
};

function authHeaders() {
  const t = localStorage.getItem("blumove_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AdminChatModeration() {
  const { t } = useTranslation();
  const [blocks, setBlocks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tamperEvents, setTamperEvents] = useState([]);
  const [tamperStats, setTamperStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Audit filters
  const [filterAction, setFilterAction] = useState("");
  const [filterTracking, setFilterTracking] = useState("");
  const [filterActor, setFilterActor] = useState("");

  const refresh = async (filters = {}) => {
    setLoading(true);
    try {
      const auditQs = new URLSearchParams();
      if (filters.action) auditQs.set("action", filters.action);
      if (filters.tracking_code) auditQs.set("tracking_code", filters.tracking_code);
      if (filters.actor) auditQs.set("actor", filters.actor);
      const auditUrl = `${API}/chat/_admin/audit-log${auditQs.toString() ? `?${auditQs.toString()}` : ""}`;

      const [b, a, te, ts] = await Promise.all([
        axios.get(`${API}/chat/_admin/blocks`, { headers: authHeaders() }),
        axios.get(auditUrl, { headers: authHeaders() }),
        axios.get(`${API}/beacons/tamper-events`).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/beacons/tamper-stats`).catch(() => ({ data: { bags: [] } })),
      ]);
      setBlocks(b.data?.blocks || []);
      setAudit(a.data?.entries || []);
      setTamperEvents(te.data?.events || []);
      setTamperStats(ts.data?.bags || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || t("moderation.fetch_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const applyFilters = () => {
    refresh({ action: filterAction, tracking_code: filterTracking, actor: filterActor });
  };

  const clearFilters = () => {
    setFilterAction("");
    setFilterTracking("");
    setFilterActor("");
    refresh();
  };

  const hasActiveFilter = !!(filterAction || filterTracking || filterActor);

  const unblock = async (name) => {
    if (!window.confirm(`${t("moderation.confirm_unblock")} ${name}?`)) return;
    try {
      await axios.delete(`${API}/chat/_admin/block-author/${encodeURIComponent(name)}`,
        { headers: authHeaders() });
      refresh();
    } catch (e) {
      setErr(t("moderation.unblock_error"));
    }
  };

  const releaseQuarantine = async (bagCode) => {
    if (!window.confirm(`Libertar a bolsa ${bagCode} da quarentena?`)) return;
    try {
      await axios.post(`${API}/beacons/${bagCode}/release-quarantine`, {},
        { headers: authHeaders() });
      refresh({ action: filterAction, tracking_code: filterTracking, actor: filterActor });
    } catch (e) {
      setErr("Erro ao libertar bolsa da quarentena");
    }
  };

  return (
    <div data-testid="admin-chat-moderation" className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.4rem" }}>{t("moderation.title")}</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>{t("moderation.subtitle")}</p>
        </div>
        <button
          onClick={refresh}
          data-testid="moderation-refresh-btn"
          className="flex items-center gap-1"
          style={{ padding: "8px 14px", borderRadius: 9999, background: "var(--surface)", border: "1px solid var(--border-color)", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600 }}
        >
          <ArrowCounterClockwise size={14} weight={loading ? "fill" : "bold"} /> {loading ? "..." : t("moderation.refresh")}
        </button>
      </div>

      {err && <p style={{ color: "var(--error)", fontSize: "0.8rem", marginBottom: 12 }} data-testid="moderation-error">{err}</p>}

      {/* Tamper events row — full width, high contrast */}
      <div className="bento-item p-5 mb-4" style={{ borderLeft: "4px solid #dc2626", background: "rgba(220,38,38,0.04)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lock size={20} weight="duotone" style={{ color: "#dc2626" }} />
            <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1rem", color: "#dc2626" }}>
              {t("moderation.tamper_title")} ({tamperEvents.length})
            </h3>
          </div>
        </div>
        {tamperEvents.length === 0 ? (
          <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{t("moderation.tamper_empty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="tamper-events-list">
            {tamperEvents.slice(0, 12).map((e) => (
              <div
                key={e.id}
                data-testid={`tamper-event-${e.id}`}
                style={{ padding: "10px 12px", borderRadius: 8, background: "var(--surface)", border: "1px solid rgba(220,38,38,0.2)" }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="bag-code" style={{ fontSize: "0.85rem", color: "#dc2626" }}>{e.bag_code}</span>
                  <span style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>
                    {new Date(e.created_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
                  </span>
                </div>
                <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                  {e.context || "tamper_detected"}
                  {e.shock_g != null && <> · {e.shock_g}g</>}
                  {e.tracking_code && <> · <span className="mono" style={{ color: "var(--primary)" }}>{e.tracking_code}</span></>}
                </div>
                {e.current_location && <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 2 }}>📍 {e.current_location}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-bag tamper stats: counter + recurring flag + quarantine release */}
      {tamperStats.length > 0 && (
        <div className="bento-item p-5 mb-4" data-testid="tamper-stats-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <WarningCircle size={18} weight="duotone" style={{ color: "var(--accent)" }} />
              <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1rem" }}>
                Bolsas com tentativas ({tamperStats.length})
              </h3>
            </div>
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
              Recorrente: &gt; 3 tentativas / 24h · Quarentena: 3+ em 60min
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="tamper-stats-list">
            {tamperStats.slice(0, 30).map((b) => {
              const flagColor = b.quarantined ? "#dc2626" : b.recurring ? "var(--accent)" : "var(--text-muted)";
              return (
                <div
                  key={b.bag_code}
                  data-testid={`tamper-stat-${b.bag_code}`}
                  style={{
                    padding: "10px 12px", borderRadius: 8,
                    background: b.quarantined ? "rgba(220,38,38,0.08)" : b.recurring ? "rgba(217,108,74,0.06)" : "var(--surface)",
                    border: `1px solid ${b.quarantined ? "rgba(220,38,38,0.3)" : b.recurring ? "rgba(217,108,74,0.25)" : "var(--border-color)"}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="bag-code" style={{ fontSize: "0.85rem", color: flagColor, fontWeight: 700 }}>{b.bag_code}</span>
                    <div className="flex items-center gap-1">
                      {b.quarantined && (
                        <span data-testid={`badge-quarantine-${b.bag_code}`} style={{ fontSize: "0.6rem", padding: "2px 7px", borderRadius: 9999, background: "#dc2626", color: "white", fontWeight: 700 }}>
                          QUARENTENA
                        </span>
                      )}
                      {b.recurring && !b.quarantined && (
                        <span data-testid={`badge-recurring-${b.bag_code}`} style={{ fontSize: "0.6rem", padding: "2px 7px", borderRadius: 9999, background: "var(--accent)", color: "white", fontWeight: 700 }}>
                          RECORRENTE
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                    <strong style={{ color: flagColor }}>{b.count_24h}</strong> em 24h ·{" "}
                    <strong>{b.count_total}</strong> total
                  </div>
                  {b.current_location && (
                    <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 2 }}>📍 {b.current_location}</div>
                  )}
                  {b.quarantined && (
                    <button
                      data-testid={`release-quarantine-${b.bag_code}`}
                      onClick={() => releaseQuarantine(b.bag_code)}
                      className="flex items-center gap-1"
                      style={{
                        marginTop: 8, padding: "5px 10px", borderRadius: 9999,
                        background: "var(--success)", color: "white", border: "none",
                        cursor: "pointer", fontSize: "0.7rem", fontWeight: 700,
                      }}
                    >
                      <ShieldCheck size={11} weight="bold" /> Libertar
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Active blocks */}
        <div className="bento-item p-5">
          <div className="flex items-center gap-2 mb-3">
            <Prohibit size={18} weight="duotone" style={{ color: "var(--accent)" }} />
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem" }}>{t("moderation.active_blocks")} ({blocks.length})</h3>
          </div>
          {blocks.length === 0 ? (
            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", padding: 12 }}>{t("moderation.no_blocks")}</p>
          ) : (
            <div className="flex flex-col gap-2" data-testid="moderation-blocks-list">
              {blocks.map((b) => (
                <div
                  key={b.author_name + (b.created_at || "")}
                  data-testid={`moderation-block-${b.author_name}`}
                  className="flex items-center justify-between"
                  style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(217,108,74,0.05)", border: "1px solid rgba(217,108,74,0.15)" }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{b.author_name}</div>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 2 }}>
                      {b.reason || t("moderation.no_reason")} · {b.blocked_by} · {new Date(b.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <button
                    data-testid={`moderation-unblock-${b.author_name}`}
                    onClick={() => unblock(b.author_name)}
                    className="flex items-center gap-1"
                    style={{ padding: "6px 12px", borderRadius: 9999, background: "var(--success)", color: "white", border: "none", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600 }}
                  >
                    <ShieldCheck size={12} weight="bold" /> {t("moderation.unblock")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Audit log */}
        <div className="bento-item p-5">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={18} weight="duotone" style={{ color: "var(--primary)" }} />
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem" }}>{t("moderation.audit_log")} ({audit.length})</h3>
          </div>
          {/* Filters */}
          <div data-testid="audit-filters" className="flex flex-wrap gap-2 mb-3" style={{ padding: 8, borderRadius: 10, background: "var(--bg)" }}>
            <select
              data-testid="audit-filter-action"
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid var(--border-color)", background: "var(--surface)", fontSize: "0.78rem", fontWeight: 600 }}
            >
              <option value="">Todas as acoes</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <input
              data-testid="audit-filter-tracking"
              type="text"
              value={filterTracking}
              onChange={(e) => setFilterTracking(e.target.value)}
              placeholder="Codigo de rastreio (BM-...)"
              style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid var(--border-color)", background: "var(--surface)", fontSize: "0.78rem", flex: "1 1 180px" }}
            />
            <input
              data-testid="audit-filter-actor"
              type="text"
              value={filterActor}
              onChange={(e) => setFilterActor(e.target.value)}
              placeholder="Autor (email/nome)"
              style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid var(--border-color)", background: "var(--surface)", fontSize: "0.78rem", flex: "1 1 160px" }}
            />
            <button
              data-testid="audit-filter-apply"
              onClick={applyFilters}
              className="flex items-center gap-1"
              style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: "var(--primary)", color: "white", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}
            >
              <Funnel size={12} weight="fill" /> Filtrar
            </button>
            {hasActiveFilter && (
              <button
                data-testid="audit-filter-clear"
                onClick={clearFilters}
                className="flex items-center gap-1"
                style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-color)", background: "var(--surface)", color: "var(--text-muted)", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}
              >
                <X size={12} weight="bold" /> Limpar
              </button>
            )}
          </div>
          {audit.length === 0 ? (
            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", padding: 12 }}>{t("moderation.no_audit")}</p>
          ) : (
            <div className="flex flex-col gap-2" style={{ maxHeight: 480, overflowY: "auto", paddingRight: 4 }} data-testid="moderation-audit-list">
              {audit.map((a) => {
                const meta = ACTION_LABELS[a.action] || { label: a.action, color: "var(--text-muted)", bg: "var(--bg)", icon: Clock };
                const Icon = meta.icon;
                return (
                  <div
                    key={a.id}
                    data-testid={`audit-entry-${a.id}`}
                    style={{ padding: "10px 14px", borderRadius: 10, background: meta.bg, border: "1px solid var(--border-color)" }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Icon size={14} weight="bold" style={{ color: meta.color }} />
                        <span style={{ fontSize: "0.78rem", fontWeight: 700, color: meta.color }}>{meta.label}</span>
                      </div>
                      <span style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>
                        {new Date(a.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      <strong>{a.actor_name || a.actor_email}</strong>
                      {a.target?.author_name && <> → {a.target.author_name}</>}
                      {a.tracking_code && <> · <span className="mono" style={{ color: "var(--primary)" }}>{a.tracking_code}</span></>}
                    </div>
                    {a.target?.body_preview && (
                      <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 4, padding: "4px 8px", borderRadius: 6, background: "var(--surface)", fontStyle: "italic" }}>
                        “{a.target.body_preview}”
                      </div>
                    )}
                    {a.target?.reason && (
                      <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 4 }}>
                        {t("moderation.reason")}: {a.target.reason}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

